/* eslint-disable react-hooks/exhaustive-deps */
import { addToast } from "components/ToastContainer/ToastStore";
import { apiUrls } from "constants/apiUrls";
import { routeConstants } from "constants/routeConstants";
import useWindowSize from "hooks/useWindowSize";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "slice";
import {
    useAddEditExperiencesMutation,
    useDeleteExperienceMutation,
    useGetExperiencesQuery,
    useGetProfileStrengthQuery,
} from "slice/user";
import { getResolvedUrl } from "utils/getResolvedUrl";
import { validations } from "utils/validations";
import { Experience } from "./types";

export const useExperienceDetails = () => {
    const [expInputs, setExpInputs] = useState<Experience>({
            category: "",
            year: "",
            nameOfProduction: "",
            roleDetails: "",
        }),
        [inputErrors, setInputErrors] = useState({
            category: "",
            year: "",
            nameOfProduction: "",
            roleDetails: "",
        }),
        [editId, setEditId] = useState(""),
        [isModalOpen, setIsModalOpen] = useState(false),
        [modalAction, setModalAction] = useState("");
    const { user_id } = useAppSelector((state) => state.userAuth);
    const {
        data,
        isSuccess: receivedExp,
        refetch,
        isFetching,
    } = useGetExperiencesQuery(
        { user_id },
        {
            skip: !user_id,
        }
    );
    const windowSize = useWindowSize();
    const [deleteExperience, { isSuccess: deletedExp }] =
        useDeleteExperienceMutation();
    const [addEditExperiences, { isLoading, isSuccess, isError, error }] =
        useAddEditExperiencesMutation();
    const { refetch: refetchProfileStrength } = useGetProfileStrengthQuery(
        user_id ?? "",
        {
            skip: !user_id,
        }
    );
    const navigateTo = useNavigate();

    const checkForErrors = useCallback(() => {
        let flag = true;
        Object.keys(inputErrors).forEach((key) => {
            const error = validations(
                key,
                expInputs[key as keyof typeof inputErrors]
            );
            if (error !== "") {
                flag = false;
            }
            setInputErrors((prev) => ({
                ...prev,
                [key]: error,
            }));
        });

        return flag;
    }, [expInputs, inputErrors]);

    const handleSubmit = useCallback(() => {
        if (checkForErrors()) {
            let payload: any = {
                method: "POST",
                user_id,
                body: {
                    experiences: [
                        {
                            ...expInputs,
                            name_of_production: expInputs.nameOfProduction,
                            role_details: expInputs.roleDetails,
                        },
                    ],
                },
            };
            if (editId) {
                payload = {
                    ...payload,
                    url: getResolvedUrl(apiUrls.modifyExperiences, {
                        user_id,
                        exp_id: editId,
                    }),
                    body: {
                        ...payload.body,
                        user_id,
                    },
                    method: "PUT",
                };
            }
            addEditExperiences(payload).then(() => {
                refetchProfileStrength();
            });
        }
    }, [user_id, expInputs, editId, checkForErrors, addEditExperiences]);

    const handleExpDelete = (expId: string) => {
        deleteExperience({ userId: user_id, expId }).then(() => {
            refetchProfileStrength();
        });
    };

    const handleExpEdit = (item: any) => {
        const { category, year, name_of_production, role_details } = item;
        setEditId(item.id);
        setExpInputs({
            category,
            year,
            nameOfProduction: name_of_production,
            roleDetails: role_details,
            id: item.id,
        });
    };

    const toggleModal = () => setIsModalOpen((prev) => !prev);

    const modalConfirmAction = () => {
        if (modalAction === "Next") {
            navigateTo(
                routeConstants.profile.editProfile.root +
                    routeConstants.profile.editProfile.otherInterests
            );
        } else if (modalAction === "Back") {
            navigateTo(
                routeConstants.profile.editProfile.root +
                    routeConstants.profile.editProfile.physicalDetails
            );
        }
    };

    const handleNavigation = (action: "Back" | "Next") => {
        toggleModal();
        setModalAction(action);
    };

    const handleInputChange = useCallback((e: any) => {
        const { name, value } = e.target;
        const error = validations(name, value);
        setExpInputs((prevState) => ({
            ...prevState,
            [name]: value,
        }));

        setInputErrors((prevState) => ({
            ...prevState,
            [name]: error,
        }));
    }, []);

    window.addEventListener("beforeunload", function (e) {
        // Check if any of the input fields are filled
        if (Object.values(expInputs).some((v) => v !== "")) {
            // Cancel the event and show alert that
            // the unsaved changes would be lost
            e.preventDefault();
            e.returnValue = "";
        }
    });

    useEffect(() => {
        if (isSuccess) {
            addToast({
                type: "success",
                text: "Experience saved successfully!",
            });
            refetch();

            setExpInputs({
                category: "",
                year: "",
                nameOfProduction: "",
                roleDetails: "",
            });
            setEditId("");
        }
    }, [isSuccess, refetch]);

    useEffect(() => {
        if (deletedExp) {
            addToast({
                type: "success",
                text: "Experience saved successfully!",
            });
            refetch();
        }
    }, [deletedExp, refetch]);

    useEffect(() => {
        if (isError) {
            addToast({
                type: "error",
                text: "Experience saved successfully!",
            });
        }
    }, [isError, error]);

    return {
        expInputs,
        inputErrors,
        data,
        receivedExp,
        isLoading,
        handleInputChange,
        isModalOpen,
        toggleModal,
        modalConfirmAction,
        isSuccess,
        editId,
        isFetching,
        windowSize,
        handleSubmit,
        handleExpDelete,
        handleExpEdit,
        handleNavigation,
    };
};
